/* eslint relay/unused-fields: "off" */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import { connect } from 'react-redux';
import { IntlContext } from 'dibs-react-intl';
import { datadog } from 'dibs-datadog/exports/datadog.ts';

// components
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader.tsx';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider.jsx';
import { ServerVarsContextConsumer } from '../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpHead } from './helpers/PdpHeadBuilder/PdpHeadDeprecated.js';
import { SharedTrackingContainer } from '../shared/SharedTrackingContainer.tsx';
import { BrowserHistoryContextProvider } from '../global/BrowserHistoryContext/BrowserHistoryContext.jsx';
import { getSkuTrackingParams } from './tracking/helpers/getSkuTrackingParams.js';
import { getPrivateOffer } from './helpers/getPrivateOffer.js';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

// helpers
import get from 'lodash/get.js';
import { getEstimationTrackingValue } from 'dibs-pricing-guidance/exports/pricingGuidanceTracking.ts';
import { trackEcommerce } from './tracking/pdpTracking.js';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds.ts';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants.ts';
import {
    sendChatMessageOnPageRefetch,
    setHasPageRefetched,
} from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { setDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { PdpSharedOriginalLanguageProvider } from './PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider.tsx';
import { useBuyerUrgency } from 'dibs-buyer-urgency/exports/useBuyerUrgency.ts';
import { pdpMetaTypes } from '../constants/pageTypeConstants.ts';
import { handlePageRefetchVariableOnUserChange } from '../shared/handlePageRefetchVariableOnUserChange.ts';
import {
    SELLER_TIER_DIAMOND,
    SELLER_TIER_PLATINUM,
    SELLER_TIER_GOLD,
    SELLER_TIER_VETTED,
} from './messages/pdpDistinctionsMessages.jsx';
import { getConsentedBuyerId } from 'dibs-cookie-jar';
import { trackSeoTests } from './helpers/trackSeoTests.ts';

class PdpSharedLayoutComponent extends Component {
    constructor(props) {
        super(props);

        this.getFollowSearchData = this.getFollowSearchData.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.trackingFunction = this.trackingFunction.bind(this);
        this.onSkuChange = this.onSkuChange.bind(this);
        this.getSkuWithId = this.getSkuWithId.bind(this);
        this.state = { skuId: null, numberOfSkuImages: null };
    }

    componentDidMount() {
        this.timeoutId = setDwelledOnPage(PAGE_TYPE.PDP);
    }

    componentDidUpdate(prevProps, prevState) {
        // if msku, determine which sku to land on
        // prioritize sku with PO if one exists for buyer
        // or use skuId in url if already specified
        // finally fall back to item's default sku id
        if (!this.state.skuId) {
            const privateOffer = this.isMultiSku()
                ? getPrivateOffer(this.props?.viewer?.transactions?.edges)
                : null;
            const privateOfferSkuId = privateOffer?.skuId || null;
            const defaultSkuId = this.props?.viewer?.itemRead?.[0]?.defaultSkuId;
            // get sku id from url hash when component mounts
            const hash = window.location.hash || '';
            const skuIdFromUrl = get(hash.match(/skuId=(\w+)/), '1');
            const skuIdFromUrlIsValid = skuIdFromUrl && !!this.getSkuWithId(skuIdFromUrl);
            const initialSkuId = skuIdFromUrlIsValid
                ? skuIdFromUrl
                : privateOfferSkuId || defaultSkuId;
            this.onSkuChange(initialSkuId);
        }

        // update url once state has been updated with proper initial sku
        if (this.isMultiSku() && this.state.skuId !== prevState.skuId) {
            const queryParams = window.location.search || '';
            // update hash when sku changes
            window.history.replaceState(
                null,
                null,
                handleLocaleUrl(
                    `${window.location.pathname}${queryParams}#skuId=${this.state.skuId}`,
                    GLOBAL_CLIENT_ONLY_LOCALE
                )
            );
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutId);
    }

    getSkuWithId(skuId) {
        const skus = get(this.props, 'viewer.itemRead.0.skus') || [];
        return skus.find(val => val.skuId === skuId);
    }

    getFollowSearchData() {
        const searchPage = this.props?.viewer?.itemRead?.[0]?.saveSearchData?.page || '';
        return { followSearchPages: [searchPage] };
    }

    isMultiSku() {
        return !!get(this.props, 'viewer.itemRead.0.isMultiSku');
    }

    handleUserRefetch(variables, renderVariables, callback) {
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: variables.isClient });
        const { viewer } = this.props;
        const item = viewer?.itemRead?.[0];
        const isSellerAccessible = item?.seller?.isAccessible;
        const fetchPdp = handlePageRefetchVariableOnUserChange(variables);

        const refetchVariables = {
            ...variables,
            ...this.getFollowSearchData(),
            fetchPdp,
            isMultiSku: this.isMultiSku(),
            localRecentHistoryItemIds,
            fetchUserRecentHistoryItem:
                localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            isInteractivePdpChat: !!isSellerAccessible, // enable chat if seller is accessible
            timezoneOffset: new Date().getTimezoneOffset(),
            personalizationId: getConsentedBuyerId(document.cookie) || '',
        };
        const updatedRenderVariables = {
            ...refetchVariables,
            ...renderVariables,
            fetchPdp: true,
        };

        this.props.doRefetch(refetchVariables, updatedRenderVariables, err => {
            if (!err) {
                setHasPageRefetched();
                sendChatMessageOnPageRefetch();
                datadog.load().then(dd => {
                    if (dd && dd.getInternalContext()) {
                        // datadog rum is initialized
                        dd.addTiming('pdp_time_to_refetch');
                    }
                });
            }
            callback(err, updatedRenderVariables);
        });
    }

    getTrackingData() {
        const item = this.props.viewer?.itemRead?.[0];
        const firstDibsEstimate = getEstimationTrackingValue({ item });

        const params = get(this.props, 'viewer.itemRead.0.pdpTrackingVpvParams') || {};
        const { additionalTrackingData } = this.props;
        const skuParams = {
            skuType: this.isMultiSku() ? 'multi' : 'single',
            skuId: this.state.skuId,
            numberOfSkuImages: this.state.numberOfSkuImages,
            ...(item?.isMultiSku && getSkuTrackingParams(params, this.state.skuId)),
        };
        const buyerDisplayablePromo = item?.activePromotion?.code;
        const { seller } = item || {};
        const { isRewardsTier2, sellerTiers, dealerSince, sellerOrderQuantity } = seller || {};
        const { isGoldTier, isPlatinumTier } = sellerTiers || {};

        let dealerStanding = SELLER_TIER_VETTED;
        if (isGoldTier) {
            dealerStanding = SELLER_TIER_GOLD;
        } else if (isPlatinumTier) {
            dealerStanding = SELLER_TIER_PLATINUM;
        } else if (isRewardsTier2) {
            dealerStanding = SELLER_TIER_DIAMOND;
        }

        return {
            ...params,
            ...additionalTrackingData,
            ...skuParams,
            soldPriceRange: firstDibsEstimate,
            firstDibsEstimate,
            hasViewInRoom: get(this.props, 'viewer.itemRead.0.viewInRoomPhotos', []).length,
            buyerUrgency: !!this.props.showBuyerUrgencyBanner,
            buyerDisplayablePromo,
            dealerStanding,
            dealerSales: sellerOrderQuantity,
            dealerYearJoined: dealerSince,
        };
    }

    trackingFunction() {
        const item = get(this.props, 'viewer.itemRead.0');
        trackEcommerce(item, this.state.skuId);
        trackSeoTests({ seoAbTestV2List: item?.seoAbTestV2List });
    }

    onSkuChange(skuId) {
        if (skuId !== this.state.skuId) {
            const sku = this.getSkuWithId(skuId);
            const numberOfSkuImages = get(sku, 'images.length');

            this.setState({
                skuId,
                numberOfSkuImages,
            });
        }
    }

    render() {
        const { user, refreshPageOnTradeLogin } = this.props;
        const { url: opt_pageURL, ...additional } = this.getTrackingData() || {};
        const gtmPageInfo = { opt_pageURL };
        const onSkuChange = this.onSkuChange;

        return (
            <BrowserHistoryContextProvider>
                <ServerVarsContextConsumer>
                    {({ clientConf, originalUrl }) => {
                        const pdpHead = new PdpHead({
                            ...this.props,
                            intl: this.context,
                            clientConf,
                            originalUrl,
                            pdpMetaType: pdpMetaTypes.available,
                        });
                        return (
                            <PdpSharedOriginalLanguageProvider
                                item={this.props.viewer?.itemRead?.[0]}
                            >
                                <SharedUserAndRelayVarLoader
                                    refreshPageOnTradeLogin={refreshPageOnTradeLogin}
                                    onUserRefetch={this.handleUserRefetch}
                                />
                                <CurrencyProvider
                                    render={({ currency, showCurrency }) => {
                                        return (
                                            <>
                                                <SharedTrackingContainer
                                                    user={user}
                                                    gtmPageInfo={gtmPageInfo}
                                                    onTrackingFired={this.trackingFunction}
                                                    additional={additional}
                                                    skuId={this.state.skuId}
                                                    isMultiSku={this.isMultiSku()}
                                                />
                                                {this.props.children({
                                                    pdpHead,
                                                    currency,
                                                    showCurrency,
                                                    selectedSkuId: this.state.skuId,
                                                    onSkuChange,
                                                })}
                                            </>
                                        );
                                    }}
                                />
                            </PdpSharedOriginalLanguageProvider>
                        );
                    }}
                </ServerVarsContextConsumer>
            </BrowserHistoryContextProvider>
        );
    }
}

PdpSharedLayoutComponent.contextType = IntlContext;

PdpSharedLayoutComponent.defaultProps = {
    additionalTrackingData: {},
};

PdpSharedLayoutComponent.propTypes = {
    children: PropTypes.func.isRequired,
    additionalTrackingData: PropTypes.object,
    doRefetch: PropTypes.func.isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    refreshPageOnTradeLogin: PropTypes.bool,
    showBuyerUrgencyBanner: PropTypes.bool,
};

function mapStateToProps({ intlVariables }) {
    return { intlVariables };
}

const withHooksHOC = Comp => {
    const Wrapper = props => {
        const { showBuyerUrgencyBanner } = useBuyerUrgency({ viewer: props.viewer });

        return <Comp {...props} showBuyerUrgencyBanner={showBuyerUrgencyBanner} />;
    };

    Wrapper.propTypes = PdpSharedLayoutComponent.propTypes;

    return Wrapper;
};

export const PdpSharedLayout = createFragmentContainer(
    connect(mapStateToProps)(withHooksHOC(PdpSharedLayoutComponent)),
    {
        viewer: graphql`
            fragment PdpSharedLayout_viewer on Viewer {
                ...PdpHeadDeprecated_viewer @relay(mask: false)
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        ...PdpSharedOriginalLanguageProvider_item
                        ...pricingGuidanceTracking_item
                        serviceId
                        isMultiSku
                        defaultSkuId
                        pdpTrackingVpvParams(
                            userId: $userId
                            userCountryCode: $userCountryCode
                            userZipCode: $userZipCode
                        ) @include(if: $isClient)
                        saveSearchData {
                            page
                        }
                        ... @include(if: $fetchPdp) {
                            ...PdpHeadDeprecated_item @relay(mask: false)
                            pdpMeta {
                                title
                            }
                            attributes {
                                period {
                                    option {
                                        name
                                    }
                                }
                            }
                            browseUrl
                            categoryCode
                            seller {
                                serviceId
                                isInternal
                                isAccessible
                                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                                dealerSince
                                sellerOrderQuantity
                                sellerTiers {
                                    isGoldTier
                                    isPlatinumTier
                                }
                            }
                            viewInRoomPhotos {
                                src
                            }
                            contemporaryTrackingString
                        }
                        skus {
                            skuId
                            images {
                                id
                            }
                        }
                        activePromotion(userId: $userId) @include(if: $isClient) {
                            code
                        }
                        seoAbTestV2List @include(if: $afterDisplay) {
                            ...trackSeoTests_abTestConfigV2UserVariant
                        }
                    }
                }
                transactions(itemId: $itemIds, buyerId: $userIds) @include(if: $isClient) {
                    edges @include(if: $isMultiSku) {
                        node {
                            skuId
                            isPrivateOffer
                            isCanceled
                        }
                    }
                }
                ...useBuyerUrgency_viewer @arguments(itemId: $itemId, userId: $userId)
            }
        `,
        user: graphql`
            fragment PdpSharedLayout_user on User {
                ...SharedTrackingContainer_user
            }
        `,
    }
);
